// src/components/panoramica/NextMatchList.js
// Prossima partita di ogni giocatore della rosa: avversario, previsione con
// forchetta, probabilità di giocare / segnare / porta inviolata e andamento
// degli ultimi fantavoti con il punto previsto.
import React, { useMemo, useState } from 'react';
import TeamBadge from '../TeamBadge';
import { RolePills } from '../auction/AuctionFormationTab';
import { getPurchaseName, getPurchaseTeam } from '../auction/panoramicaEngine';
import { formatFixtureDate } from '../../data/serieACalendar';
import { fvTone } from './forecastEngine';

const ROLE_RANK = { P: 0, D: 1, C: 2, A: 3 };
const SCALE_MIN = 2;
const SCALE_MAX = 13;
const pos = (x) => `${Math.max(0, Math.min(100, ((x - SCALE_MIN) / (SCALE_MAX - SCALE_MIN)) * 100))}%`;

export function Sparkline({ last = [], forecast }) {
  const pts = [...last.map((l) => l.fmark), forecast];
  if (pts.length < 2) return <span className="pz-spark pz-spark--empty">—</span>;
  const W = 92; const H = 28; const lo = Math.min(4, ...pts); const hi = Math.max(9, ...pts);
  const x = (i) => (i / (pts.length - 1)) * (W - 6) + 3;
  const y = (val) => H - 3 - ((val - lo) / (hi - lo)) * (H - 6);
  const real = last.map((l, i) => `${x(i)},${y(l.fmark)}`).join(' ');
  const lastIdx = last.length - 1;
  return (
    <svg className="pz-spark" viewBox={`0 0 ${W} ${H}`} width={W} height={H} aria-hidden="true">
      <line x1="0" x2={W} y1={y(6)} y2={y(6)} className="pz-spark__six" />
      {last.length > 1 && <polyline points={real} className="pz-spark__line" />}
      {lastIdx >= 0 && <line x1={x(lastIdx)} y1={y(last[lastIdx].fmark)} x2={x(pts.length - 1)} y2={y(forecast)} className="pz-spark__dash" />}
      {last.map((l, i) => <circle key={i} cx={x(i)} cy={y(l.fmark)} r="1.8" className="pz-spark__dot" />)}
      <circle cx={x(pts.length - 1)} cy={y(forecast)} r="3" className="pz-spark__fc" />
    </svg>
  );
}

export function RangeBar({ f }) {
  return (
    <span className="pz-range" aria-label={`forchetta ${f.p10.toFixed(1)}–${f.p90.toFixed(1)}`}>
      <span className="pz-range__six" style={{ left: pos(6) }} />
      <span className="pz-range__band" style={{ left: pos(f.p10), right: `calc(100% - ${pos(f.p90)})` }} />
      <span className="pz-range__dot" style={{ left: pos(f.expFV) }} />
    </span>
  );
}

const SORTS = [
  { key: 'role', label: 'Ruolo' },
  { key: 'fv', label: 'Previsione' },
  { key: 'play', label: 'Prob. di giocare' },
];

export default function NextMatchList({ items, rolesMode, starterIds, onSelect }) {
  const [sort, setSort] = useState('role');
  const sorted = useMemo(() => {
    const list = [...items];
    if (sort === 'fv') list.sort((a, b) => b.forecast.expFV - a.forecast.expFV);
    else if (sort === 'play') list.sort((a, b) => b.forecast.pPlay - a.forecast.pPlay);
    else list.sort((a, b) => (ROLE_RANK[a.forecast.role] - ROLE_RANK[b.forecast.role]) || b.forecast.expFV - a.forecast.expFV);
    return list;
  }, [items, sort]);

  return (
    <>
      <div className="pz-segment" role="group" aria-label="Ordina">
        {SORTS.map((s) => (
          <button key={s.key} type="button" className={sort === s.key ? 'is-active' : ''} onClick={() => setSort(s.key)}>{s.label}</button>
        ))}
      </div>
      <div className="pz-next">
        {sorted.map(({ purchase, forecast: f }) => {
          const fx = f.fixture;
          const team = getPurchaseTeam(purchase);
          const isStarter = starterIds.has(f.playerId);
          const out = f.pPlay === 0;
          return (
            <button
              key={f.playerId}
              type="button"
              className={`pz-next__row ${isStarter ? 'is-starter' : ''} ${out ? 'is-out' : ''}`}
              onClick={() => onSelect({ purchase, forecast: f })}
            >
              <span className="pz-next__who">
                <TeamBadge team={team} size={28} showTooltip={false} />
                <span className="pz-next__name">
                  <strong>{getPurchaseName(purchase)}</strong>
                  <span><RolePills purchase={purchase} rolesMode={rolesMode} />{isStarter && <em className="pz-tag-xi">XI</em>}</span>
                </span>
              </span>
              <span className="pz-next__match">
                {fx ? (
                  <>
                    <TeamBadge team={fx.opponent} size={20} showTooltip={false} />
                    <span>
                      <strong>{fx.home ? 'vs' : '@'} {fx.opponent}</strong>
                      <em>G{fx.giornata} · {formatFixtureDate(fx.date)}</em>
                    </span>
                  </>
                ) : <em>Nessuna partita</em>}
              </span>
              <span className="pz-next__fc">
                <b className={fvTone(f)}>{out ? '—' : f.expFV.toFixed(1)}</b>
                {!out && <RangeBar f={f} />}
              </span>
              <span className="pz-next__probs">
                <span><i>Gioca</i>{Math.round(f.pPlay * 100)}%</span>
                {f.role === 'P' || f.role === 'D'
                  ? <span><i>Porta invi.</i>{Math.round(f.pCleanSheet * 100)}%</span>
                  : <span><i>Gol</i>{Math.round(f.pGoal * 100)}%</span>}
              </span>
              <span className="pz-next__trend"><Sparkline last={f.last} forecast={f.expFV} /></span>
            </button>
          );
        })}
      </div>
    </>
  );
}
